import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Cohort, Course } from '@juvo/schemas';
import { formatDateRange, pickLocale } from '@juvo/utils';
import { store } from '../store.js';
import '../layouts/site-chrome.js';

@customElement('jv-course-detail-page')
export class JvCourseDetailPage extends LitElement {
  static styles = css`
    h1 {
      font-size: var(--jv-fs-display);
      margin: 0.5rem 0 1rem;
    }
    .lede {
      max-width: 40rem;
      color: var(--jv-color-muted);
      margin-bottom: 1.5rem;
    }
    .meta {
      display: flex;
      gap: 0.4rem;
      flex-wrap: wrap;
      margin-bottom: 1rem;
    }
    .hint {
      color: var(--jv-color-muted);
      font-size: var(--jv-fs-sm);
      margin: 0 0 1.5rem;
    }
    .cohorts {
      display: grid;
      gap: 1rem;
    }
    @media (min-width: 800px) {
      .cohorts {
        grid-template-columns: 1fr 1fr;
      }
    }
    h2 {
      font-size: var(--jv-fs-xl);
    }
  `;

  @property() slug = '';
  @state() private course: Course | null = null;
  @state() private cohorts: Cohort[] = [];
  @state() private message = '';
  @state() private loading = true;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    this.load();
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  updated(changed: Map<string, unknown>) {
    if (changed.has('slug') && this.slug) this.load();
  }

  private async load() {
    if (!this.slug) return;
    this.loading = true;
    const detail = await store.client.getCourse(this.slug);
    this.course = detail?.course ?? null;
    this.cohorts = detail?.cohorts ?? [];
    this.loading = false;
  }

  render() {
    const t = store.t.bind(store);
    if (this.loading) {
      return html`<jv-page><p class="lede">…</p></jv-page>`;
    }
    if (!this.course) {
      return html`<jv-page><jv-empty-state heading=${t('notfound')}></jv-empty-state></jv-page>`;
    }
    return html`
      <jv-page>
        <div class="meta">
          <jv-badge>${this.course.track}</jv-badge>
          <jv-badge tone="sand">${this.course.programmeType}</jv-badge>
          <jv-badge tone="sand">${this.course.certificateType}</jv-badge>
        </div>
        <h1>${pickLocale(this.course.title, store.locale)}</h1>
        <p class="lede">${pickLocale(this.course.description, store.locale)}</p>
        <p class="hint">
          ${this.course.durationWeeks} ${t('course.weeks')} · ${this.course.location} ·
          ${t('course.needAccount')}
        </p>
        <h2>${t('course.cohorts')}</h2>
        <div class="cohorts">
          ${this.cohorts.map(
            (cohort) => html`
              <jv-card>
                <p>${formatDateRange(cohort.startDate, cohort.endDate, store.locale)}</p>
                <p>${cohort.location} · ${cohort.language.toUpperCase()}</p>
                <jv-progress
                  label=${t('course.seats')}
                  .value=${cohort.seatsTaken}
                  .max=${cohort.capacity}
                ></jv-progress>
                <jv-button @click=${() => this.enroll(cohort.id)}>${t('course.enroll')}</jv-button>
              </jv-card>
            `,
          )}
        </div>
        ${this.message ? html`<p class="hint">${this.message}</p>` : ''}
      </jv-page>
    `;
  }

  private async enroll(cohortId: string) {
    if (!store.user) {
      history.pushState({}, '', `/login?next=/courses/${this.slug}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    try {
      const result = await store.client.enroll({ cohortId, paymentMethod: 'direct' });
      this.message = result.status;
      await this.load();
    } catch (error) {
      this.message = error instanceof Error ? error.message : 'Error';
    }
  }
}
